import { ArrowDown, ArrowUp, ChevronsUpDown, Inbox, Video } from "lucide-react";
import { useMemo, useState } from "react";

import { EmptyState } from "@/components/common/empty-state";
import { TableSkeleton } from "@/components/common/loading-skeleton";
import { TablePagination } from "@/components/common/table-pagination";
import { DoctorStatusBadge } from "@/components/common/status-badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDate, initials } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { Doctor } from "@/types/doctor";

type SortKey = "name" | "specialization" | "experience" | "createdAt";

const COLUMNS: Array<{ key: SortKey | null; label: string; className?: string }> = [
  { key: "name", label: "Doctor" },
  { key: "specialization", label: "Specialization" },
  { key: "experience", label: "Experience" },
  { key: null, label: "Hospital" },
  { key: null, label: "Video" },
  { key: null, label: "Email" },
  { key: "createdAt", label: "Registered" },
  { key: null, label: "Status" },
  { key: null, label: "Actions", className: "text-right" },
];

const PAGE_SIZE = 8;

export function PendingDoctorsTable({
  doctors,
  loading,
  search,
  onOpenDoctor,
  onApprove,
  onReject,
}: {
  doctors: Doctor[];
  loading: boolean;
  search: string;
  onOpenDoctor: (doctor: Doctor) => void;
  onApprove: (doctor: Doctor) => void;
  onReject: (doctor: Doctor) => void;
}) {
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({
    key: "createdAt",
    dir: "asc",
  });
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const base = term
      ? doctors.filter((doctor) =>
          [doctor.name, doctor.specialization, doctor.hospitalName, doctor.email, doctor.location]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(term)),
        )
      : doctors;

    return [...base].sort((a, b) => {
      const dir = sort.dir === "asc" ? 1 : -1;
      if (sort.key === "experience") return (a.experience - b.experience) * dir;
      if (sort.key === "createdAt")
        return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dir;
      return String(a[sort.key] ?? "").localeCompare(String(b[sort.key] ?? "")) * dir;
    });
  }, [doctors, search, sort]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const start = (currentPage - 1) * PAGE_SIZE;
  const rows = filtered.slice(start, start + PAGE_SIZE);

  const toggleSort = (key: SortKey) => {
    setPage(1);
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" },
    );
  };

  if (!loading && filtered.length === 0) {
    return (
      <div className="card-surface">
        <EmptyState
          icon={Inbox}
          title={
            search.trim()
              ? "No doctors match your search"
              : "All doctor registrations have been reviewed"
          }
          description={
            search.trim()
              ? "Try a different name, specialization, hospital or email."
              : "There are currently no doctors waiting for approval."
          }
        />
      </div>
    );
  }

  return (
    <div className="card-surface overflow-hidden">
      {/* Desktop / tablet table */}
      <div className="hidden overflow-x-auto md:block">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {COLUMNS.map((column) => (
                <TableHead
                  key={column.label}
                  className={cn("text-xs whitespace-nowrap", column.className)}
                >
                  {column.key ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(column.key as SortKey)}
                      className="inline-flex items-center gap-1 font-semibold transition-colors hover:text-foreground"
                    >
                      {column.label}
                      {sort.key === column.key ? (
                        sort.dir === "asc" ? (
                          <ArrowUp className="size-3" />
                        ) : (
                          <ArrowDown className="size-3" />
                        )
                      ) : (
                        <ChevronsUpDown className="size-3 opacity-40" />
                      )}
                    </button>
                  ) : (
                    column.label
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableSkeleton rows={6} columns={COLUMNS.length} />
            ) : (
              rows.map((doctor) => (
                <TableRow
                  key={doctor.id}
                  className="cursor-pointer transition-colors"
                  onClick={() => onOpenDoctor(doctor)}
                >
                  <TableCell className="py-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="size-9 border border-border">
                        {doctor.profilePicture && (
                          <AvatarImage src={doctor.profilePicture} alt="" />
                        )}
                        <AvatarFallback className="bg-primary text-xs font-semibold text-primary-foreground">
                          {initials(doctor.name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium whitespace-nowrap">{doctor.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{doctor.specialization}</TableCell>
                  <TableCell className="tabular-nums">{doctor.experience} yrs</TableCell>
                  <TableCell className="max-w-40 truncate text-muted-foreground">
                    {doctor.hospitalName ?? "—"}
                  </TableCell>
                  <TableCell>
                    {doctor.videoConsultationAvailable ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-info">
                        <Video className="size-3.5" aria-hidden="true" /> Yes
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">No</span>
                    )}
                  </TableCell>
                  <TableCell className="max-w-48 truncate text-muted-foreground">
                    {doctor.email ?? "—"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {formatDate(doctor.createdAt)}
                  </TableCell>
                  <TableCell>
                    <DoctorStatusBadge status={doctor.status} />
                  </TableCell>
                  <TableCell className="text-right" onClick={(event) => event.stopPropagation()}>
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-lg"
                        onClick={() => onReject(doctor)}
                      >
                        Reject
                      </Button>
                      <Button size="sm" className="rounded-lg" onClick={() => onApprove(doctor)}>
                        Approve
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile cards */}
      <div className="divide-y divide-border md:hidden">
        {loading
          ? Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="space-y-3 p-4">
                <div className="h-4 w-40 animate-pulse rounded bg-muted" />
                <div className="h-3 w-24 animate-pulse rounded bg-muted" />
              </div>
            ))
          : rows.map((doctor) => (
              <div key={doctor.id} className="space-y-3 p-4">
                <button
                  type="button"
                  className="flex w-full items-center gap-3 text-left"
                  onClick={() => onOpenDoctor(doctor)}
                >
                  <Avatar className="size-10 border border-border">
                    {doctor.profilePicture && <AvatarImage src={doctor.profilePicture} alt="" />}
                    <AvatarFallback className="bg-primary text-xs font-semibold text-primary-foreground">
                      {initials(doctor.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{doctor.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {doctor.specialization} · {doctor.experience} yrs ·{" "}
                      {formatDate(doctor.createdAt)}
                    </span>
                  </span>
                  <DoctorStatusBadge status={doctor.status} />
                </button>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-10 flex-1"
                    onClick={() => onReject(doctor)}
                  >
                    Reject
                  </Button>
                  <Button size="sm" className="h-10 flex-1" onClick={() => onApprove(doctor)}>
                    Approve
                  </Button>
                </div>
              </div>
            ))}
      </div>

      {!loading && (
        <TablePagination
          page={currentPage}
          pageCount={pageCount}
          total={filtered.length}
          from={start + 1}
          to={start + rows.length}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
